import React, { createContext, useContext, useState } from 'react';

type MessageStatus = 'loading' | 'loaded' | 'empty';

type ChatContextType = {
  isOpen: boolean;
  messageStatus: MessageStatus;
  setMessageStatus: (value: MessageStatus) => void;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messageStatus, setMessageStatus] = useState<MessageStatus>('empty');

  const contextValue = React.useMemo(
    () => ({
      isOpen,
      messageStatus,
      setMessageStatus,
      openChat: () => setIsOpen(true),
      closeChat: () => setIsOpen(false),
      toggleChat: () => setIsOpen((prev) => !prev),
    }),
    [isOpen, messageStatus],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
